import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Initialize state from localStorage to persist across refreshes
  const [user, setUser] = useState(() => {
    // Try to restore user from localStorage if token exists
    const token = localStorage.getItem('token');
    if (token) {
      // Return a default user object based on token presence
      return { username: 'admin' };
    }
    return null;
  });
  
  // Initialize loading as false if token exists (instant restore)
  const [loading, setLoading] = useState(() => {
    return !localStorage.getItem('token');
  });
  
  // Initialize isAuthenticated from token presence to prevent logout on refresh
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('token');
  });

  useEffect(() => {
    // Only check auth if we need to verify (token exists but we haven't verified yet)
    const token = localStorage.getItem('token');
    if (token) {
      // User is already authenticated via token, verify in background silently
      verifyTokenSilently();
    } else {
      // No token, user is not authenticated
      setLoading(false);
    }
  }, []);

  const verifyTokenSilently = async () => {
    // Verify token in background without affecting auth state
    // Only logout if we get a clear 401 response
    try {
      const response = await authAPI.verify();
      if (response && response.user) {
        setUser(response.user);
      }
    } catch (verifyError) {
      // Check if error has status code or if error message indicates 401
      const is401 = verifyError.status === 401 || 
                    verifyError.statusText === 'Unauthorized' ||
                    (verifyError.message && (verifyError.message.includes('401') || 
                                           verifyError.message.includes('Unauthorized') ||
                                           verifyError.message.includes('Invalid token')));
      
      if (is401) {
        // Token is invalid - clear it silently without redirect
        // Don't log - this is expected when token is missing/invalid
        localStorage.removeItem('token');
        setIsAuthenticated(false);
        setUser(null);
      }
      // For all other errors (network, server down, etc), keep session alive
      // Don't log errors - this is normal background verification
    }
  };

  const checkAuth = async () => {
    // This function is kept for compatibility but mostly relies on localStorage
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
      verifyTokenSilently();
    } else {
      setIsAuthenticated(false);
      setUser(null);
    }
    setLoading(false);
  };

  const login = async (username, password) => {
    try {
      const response = await authAPI.login(username, password);
      if (response.token) {
        localStorage.setItem('token', response.token);
      }
      setUser(response.user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await authAPI.changePassword(currentPassword, newPassword);
      return { success: true, message: response.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const changeUsername = async (newUsername, password) => {
    try {
      const response = await authAPI.changeUsername(newUsername, password);
      if (response.token) {
        localStorage.setItem('token', response.token);
      }
      if (response.user) {
        setUser(response.user);
      }
      return { success: true, message: response.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
    changePassword,
    changeUsername,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

